import { DatePipe, NgFor, NgIf } from '@angular/common';
import {
	AfterViewInit,
	Component,
	Inject,
	OnDestroy,
	OnInit
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SocialListeningPlatformIconComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/mention-cards-section/social-listening-platform-icon.component';
import { SocialListeningPlatformType } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/platform-chips/platform-chip.component';
import {
	SocialListeningMention,
	SocialListeningMentionsRequestBody
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-mentions.model';
import { GetSocialListeningMyBrandMentionsService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-mentions.service';
import { formatPlatformLabel } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/analytics-label.utils';
import {
	CloseOverlayButtonComponent,
	PrimaryInputComponent,
	TypographyComponent
} from 'components';
import { Subject, Subscription } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	finalize,
	map,
	takeUntil
} from 'rxjs/operators';
import { ReportDateRange } from '../../../../models/preview/preview.models';
import { quoteExcerpt } from '../../../../services/report-quote';

export interface ReportQuoteSwapDialogData {
	brandId: string;
	/** The report's window: the list holds that period's mentions. */
	range: ReportDateRange | null;
	/** The mention on the card now, which the list marks rather than offers. */
	currentMentionId: string | null;
}

/** One row of the list, prepared once so the template only reads fields. */
interface SwapOption {
	id: string;
	author: string;
	platform: SocialListeningPlatformType;
	platformLabel: string;
	postedAt: string | null;
	excerpt: string;
	current: boolean;
}

/** How many mentions a page of the list holds. */
const PAGE_SIZE = 20;

/**
 * Swap mention: another of the brand's mentions for a quote card.
 *
 * Read from the Mentions list itself, over the report's own window and with
 * exact matching, as the report counts — the newest first, and a search over
 * the words for the one somebody remembers. It only chooses: the card hands
 * the pick to the page, which puts it in the spec, reads the card again and
 * saves it by itself.
 *
 * The list service answers a failed read with an empty page, so the empty
 * state says there is nothing to show rather than that there is nothing.
 */
@Component({
	standalone: true,
	selector: 'app-report-quote-swap-dialog',
	templateUrl: './report-quote-swap-dialog.component.html',
	styleUrls: ['./report-quote-swap-dialog.component.scss'],
	imports: [
		NgFor,
		NgIf,
		DatePipe,
		CloseOverlayButtonComponent,
		PrimaryInputComponent,
		TypographyComponent,
		SocialListeningPlatformIconComponent
	]
})
export class ReportQuoteSwapDialogComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	readonly searchControl = new FormControl<string>('');
	/** The search box focuses itself when this fires. */
	readonly focusSearch = new Subject<void>();

	options: SwapOption[] = [];
	loading = true;
	hasMore = false;
	query = '';

	private page = 1;
	private request: Subscription | null = null;
	private unsubscriber = new Subject<void>();

	constructor(
		private dialogRef: MatDialogRef<ReportQuoteSwapDialogComponent, string>,
		private mentionsService: GetSocialListeningMyBrandMentionsService,
		@Inject(MAT_DIALOG_DATA) private data: ReportQuoteSwapDialogData
	) {}

	ngOnInit(): void {
		this.searchControl.valueChanges
			.pipe(
				map(value => (value ?? '').trim()),
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this.unsubscriber)
			)
			.subscribe(query => {
				this.query = query;
				this.load(1);
			});
		this.load(1);
	}

	ngAfterViewInit(): void {
		this.focusSearch.next();
	}

	pick(option: SwapOption): void {
		if (!option.current) {
			this.dialogRef.close(option.id);
		}
	}

	showMore(): void {
		if (!this.loading && this.hasMore) {
			this.load(this.page + 1);
		}
	}

	close(): void {
		this.dialogRef.close();
	}

	trackById(_index: number, option: SwapOption): string {
		return option.id;
	}

	ngOnDestroy(): void {
		this.request?.unsubscribe();
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	/**
	 * One page of the list. A new search replaces the read still out for the
	 * last one, so a slow answer to an old query never lands over a new one.
	 */
	private load(page: number): void {
		this.request?.unsubscribe();
		this.loading = true;
		this.request = this.mentionsService
			.fetch(this.data.brandId, this.body(page))
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(response => {
				const rows = response.mentions
					.filter(mention => mention.id)
					.map(mention => this.option(mention));
				this.options = page === 1 ? rows : [...this.options, ...rows];
				this.page = page;
				this.hasMore = page < response.pagination.total_pages;
			});
	}

	/** The report's window in whole days, as the Mentions list takes it. */
	private body(page: number): SocialListeningMentionsRequestBody {
		const range = this.data.range;
		return {
			page_number: page,
			page_size: PAGE_SIZE,
			sort_by: 'date',
			match_type: 'exact',
			date_from: range ? range.start.slice(0, 10) : undefined,
			date_to: range ? range.end.slice(0, 10) : undefined,
			keyword: this.query || undefined
		};
	}

	private option(mention: SocialListeningMention): SwapOption {
		return {
			id: mention.id,
			author: mention.authorName || mention.authorUsername,
			platform: mention.authorPlatform,
			platformLabel: formatPlatformLabel(mention.authorPlatform),
			postedAt: mention.postDate || null,
			excerpt: quoteExcerpt(mention.postCaption || mention.title, 160),
			current: mention.id === this.data.currentMentionId
		};
	}
}
